import {
  Card, Descriptions, Empty, Space, Spin, Tag, Typography,
} from 'antd';
import rideBookingApi from 'api/rideBookingApi';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import {
  formatBookingDate, formatBookingMoney, getRideBookingErrorMessage, RideBookingStatusTag,
} from '../../rideBookingsConstants';

const { Text } = Typography;

function RideBookingsDetailScreen() {
  const router = useRouter();
  const bookingId = router.query?.bookingId;

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!bookingId) return;
    setDetailLoading(true);
    try {
      const response = await rideBookingApi.getBooking(bookingId);
      setDetail(response?.booking || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRideBookingErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          {
            title: 'Booking xe ghép',
            onPress: () => router.navigate(-1),
          },
          { title: detail ? `Booking ${detail.bookingId}` : 'Chi tiết booking' },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={detailLoading}>
            {!detail && !detailLoading ? <Empty description="Không tải được booking" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Trạng thái"><RideBookingStatusTag status={detail.status} /></Descriptions.Item>
                  <Descriptions.Item label="Chuyến">
                    {detail.tripCode}
                    {' '}
                    (
                    {detail.tripStatus || '—'}
                    )
                  </Descriptions.Item>
                  <Descriptions.Item label="Khách">
                    {detail.customer?.fullname || `User #${detail.customerId}`}
                    {' · '}
                    {detail.customer?.phone || detail.customer?.email || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tài xế">
                    {detail.driver?.fullname || '—'}
                    {' · '}
                    {detail.driver?.phone || detail.driver?.email || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Điểm đón">{detail.pickup?.address}</Descriptions.Item>
                  <Descriptions.Item label="Điểm trả">{detail.dropoff?.address}</Descriptions.Item>
                  <Descriptions.Item label={detail.isCharter ? 'Dịch vụ' : 'Ghế'}>{detail.isCharter ? <Tag color="gold">Bao xe {detail.charterVehicleCapacity || 4} chỗ</Tag> : detail.seats}</Descriptions.Item>
                  <Descriptions.Item label={detail.isCharter ? 'Giá bao xe' : 'Giá mỗi ghế'}>{formatBookingMoney(detail.isCharter ? detail.totalFare : detail.farePerSeat)}</Descriptions.Item>
                  <Descriptions.Item label="Tổng tiền">{formatBookingMoney(detail.totalFare)}</Descriptions.Item>
                  <Descriptions.Item label="Giữ ghế đến">{formatBookingDate(detail.holdExpiresAt)}</Descriptions.Item>
                  <Descriptions.Item label="Khởi hành">{formatBookingDate(detail.tripDepartureAt)}</Descriptions.Item>
                  <Descriptions.Item label="Đón khách">{formatBookingDate(detail.pickedUpAt)}</Descriptions.Item>
                  <Descriptions.Item label="Trả khách">{formatBookingDate(detail.droppedOffAt)}</Descriptions.Item>
                  <Descriptions.Item label="Tạo lúc">{formatBookingDate(detail.createdAt)}</Descriptions.Item>
                </Descriptions>
                {detail.status === 'REJECTED' && (
                  <Card size="small" title="Tài xế từ chối">
                    <Text>{detail.driverResponseReason || 'Không có lý do'}</Text>
                  </Card>
                )}
                {detail.status === 'CANCELLED' && (
                  <Card size="small" title="Hủy booking">
                    <Text>{detail.cancellationReason || 'Không có lý do'}</Text>
                  </Card>
                )}
                {detail.status === 'PICKUP_FAILED' && (
                  <Card size="small" title="Đón khách thất bại">
                    <Text>{detail.pickupFailureReason || 'Không có lý do'}</Text>
                    <br />
                    <Text type="secondary">Ghi nhận lúc: {formatBookingDate(detail.pickupFailedAt)}</Text>
                  </Card>
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
}

export default RideBookingsDetailScreen;
